/**
 * @lodex-pro/web-portal/routes/DeviceReadings — the operator admin's Device readings page (Phase
 * 13 / Task 13.13; D-12, D-13, D-21c, D-28).
 *
 *   - Gateways: the company's MQTT gateways, read-only (Paelode adds, revokes and rotates them).
 *   - Unmatched: readings from a gateway or an API key that named no tank of the company, or
 *     failed a check, so the producer tank ID or tag can be fixed in Tank setup.
 *   - The instruction sheet a producer configures its gateways from.
 *
 * OPERATOR_ADMIN only (MANAGE_OPERATOR_TANKS on the API). Times are local (DEC-004), never UTC.
 */

import React from 'react';
import type {
  MqttGatewayStatus,
  MqttGatewayView,
} from '@lodex-pro/shared/validation/mqtt-gateways';
import type {
  DeviceReadingIssue,
  DeviceReadingIssueReason,
  DeviceReadingKeyKind,
} from '@lodex-pro/shared/validation/operator-portal-device-readings';
import { usePortalMe } from '../hooks/useOperatorPortal';
import {
  useDeviceConnection,
  useDeviceGateways,
  useDeviceReadingIssues,
} from '../hooks/useDeviceReadings';
import { PortalDataError } from '../components/PortalDataError';
import { portalIsAdmin } from '../nav/portal-features';
import { localDateTime } from './ApiUsage';
import { MqttInstructionSheet } from './MqttInstructionSheet';

// ── Words ────────────────────────────────────────────────────────────────────

export const NOT_ADMIN_MESSAGE = "Only your company's portal admin can see device readings.";
export const MQTT_OFF_MESSAGE = 'MQTT readings are not on your plan.';
export const NO_GATEWAYS_MESSAGE = 'No gateways yet. Paelode sets them up: ask your lodeX contact.';
export const UNMATCHED_HELP =
  "Fix it by setting the tank's Producer tank ID (or MQTT tag) in Tank setup.";

export const STATUS_LABELS: Readonly<Record<MqttGatewayStatus, string>> = {
  ACTIVE: 'Active',
  REVOKED: 'Revoked',
  PENDING: 'Setting up',
  FAILED: 'Failed',
};

export const KEY_KIND_LABELS: Readonly<Record<DeviceReadingKeyKind, string>> = {
  TANK_ID: 'Tank ID',
  TAG: 'Tag',
  ID: 'Internal id',
  NONE: '—',
};

export const PROBLEM_TEXT: Readonly<Record<DeviceReadingIssueReason, string>> = {
  UNKNOWN_TANK_ID: 'No tank has this producer tank ID',
  UNKNOWN_TAG: 'No tank has this MQTT tag',
  UNKNOWN_ID: 'No tank of yours has this id',
  TANK_INACTIVE: 'The tank is deactivated',
  TANK_KEY_MISMATCH: 'The tank ID and tag point to different tanks',
  BAD_VALUE: 'Send exactly one of level_in or volume_bbl',
  CLOCK_AHEAD: "The reading's time is in the future: check the gateway clock",
  TOO_OLD: 'Older than 30 days',
  OVER_CAPACITY: 'More than the tank holds',
  BAD_PAYLOAD: "The message isn't valid JSON in the documented format",
};

/** "3 min ago", "2 h ago", "4 days ago"; "just now" under a minute; future times as "just now". */
export function relativeAgo(iso: string, now: Date = new Date()): string {
  const s = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${String(h)} h ago`;
  const d = Math.floor(h / 24);
  return `${String(d)} days ago`;
}

/** Where an unmatched reading came from: the gateway's label, or the API key's label and id. */
export function issueSource(i: DeviceReadingIssue): string {
  if (i.gatewayLabel !== null) return i.gatewayLabel;
  if (i.keyLabel !== null) return `API key ${i.keyLabel} (${i.keyId ?? ''})`;
  return '—';
}

// ── Styles (as the Webhooks page) ────────────────────────────────────────────

const CARD: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '16px',
  marginBottom: '16px',
};
const MUTED: React.CSSProperties = { color: 'var(--color-text-tertiary)' };
const CELL: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-border-primary)',
  verticalAlign: 'top',
};
const MONO: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '13px',
};
const BADGE: React.CSSProperties = {
  marginLeft: '8px',
  padding: '1px 6px',
  borderRadius: '4px',
  fontSize: '12px',
  background: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};

function When({ iso }: { readonly iso: string | null }): React.JSX.Element {
  if (iso === null) return <span style={MUTED}>Never</span>;
  return (
    <span>
      {localDateTime(iso)} <span style={MUTED}>({relativeAgo(iso)})</span>
    </span>
  );
}

// ── Gateways ─────────────────────────────────────────────────────────────────

function GatewaysCard({
  enabled,
  gateways,
}: {
  readonly enabled: boolean;
  readonly gateways: readonly MqttGatewayView[];
}): React.JSX.Element {
  return (
    <section aria-label="Gateways" style={CARD}>
      <h2 style={{ marginTop: 0, fontSize: '18px' }}>Gateways</h2>
      {!enabled ? (
        <p role="status">{MQTT_OFF_MESSAGE}</p>
      ) : gateways.length === 0 ? (
        <p role="status">{NO_GATEWAYS_MESSAGE}</p>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={CELL}>Gateway</th>
              <th style={CELL}>Status</th>
              <th style={CELL}>Last seen</th>
              <th style={CELL}>Client ID</th>
            </tr>
          </thead>
          <tbody>
            {gateways.map((g) => (
              <tr key={g.id} data-testid={`gateway-${g.id}`}>
                <td style={CELL}>
                  {g.label}
                  {g.rotating && <span style={BADGE}>Rotating</span>}
                </td>
                <td style={CELL}>{STATUS_LABELS[g.status]}</td>
                <td style={CELL}>
                  <When iso={g.lastSeenAt} />
                </td>
                <td style={{ ...CELL, ...MONO }}>{g.thingName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p style={{ ...MUTED, fontSize: '13px', marginBottom: 0 }}>
        Paelode adds, replaces and turns off gateways. Ask your lodeX contact for a change.
      </p>
    </section>
  );
}

// ── Unmatched ────────────────────────────────────────────────────────────────

function UnmatchedCard({
  enabled,
  issues,
}: {
  readonly enabled: boolean;
  readonly issues: readonly DeviceReadingIssue[];
}): React.JSX.Element {
  return (
    <section aria-label="Unmatched readings" style={CARD}>
      <h2 style={{ marginTop: 0, fontSize: '18px' }}>Unmatched readings</h2>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0 }}>
        Readings from your gateways or API keys that we could not store against one of your tanks.{' '}
        {UNMATCHED_HELP}
      </p>
      {!enabled ? (
        <p role="status">Device readings are not on your plan.</p>
      ) : issues.length === 0 ? (
        <p role="status">Nothing unmatched. Every reading found its tank.</p>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={CELL}>Tank ID or tag</th>
              <th style={CELL}>Problem</th>
              <th style={CELL}>From</th>
              <th style={CELL}>First seen</th>
              <th style={CELL}>Last seen</th>
              <th style={CELL}>Times</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((i) => (
              <tr key={`${i.keyKind}:${i.normalizedKey}:${i.reason}:${issueSource(i)}`}>
                <td style={CELL}>
                  <span style={MUTED}>{KEY_KIND_LABELS[i.keyKind]}</span>{' '}
                  {i.normalizedKey !== '' && <span style={MONO}>{i.normalizedKey}</span>}
                </td>
                <td style={CELL}>{PROBLEM_TEXT[i.reason]}</td>
                <td style={CELL}>{issueSource(i)}</td>
                <td style={CELL}>
                  <When iso={i.firstSeenAt} />
                </td>
                <td style={CELL}>
                  <When iso={i.lastSeenAt} />
                </td>
                <td style={CELL}>{i.seenCount.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function DeviceReadingsBody(): React.JSX.Element {
  const gateways = useDeviceGateways();
  const issues = useDeviceReadingIssues();
  const connection = useDeviceConnection();
  return (
    <>
      {gateways.isError ? (
        <PortalDataError error={gateways.error} fallback="Could not load your gateways." />
      ) : gateways.data === undefined ? (
        <p role="status">Loading…</p>
      ) : (
        <GatewaysCard enabled={gateways.data.enabled} gateways={gateways.data.gateways} />
      )}
      {issues.isError ? (
        <PortalDataError error={issues.error} fallback="Could not load unmatched readings." />
      ) : issues.data === undefined ? (
        <p role="status">Loading…</p>
      ) : (
        <UnmatchedCard enabled={issues.data.enabled} issues={issues.data.issues} />
      )}
      {connection.data?.enabled === true && gateways.data !== undefined && (
        <div style={CARD}>
          <MqttInstructionSheet connection={connection.data} gateways={gateways.data.gateways} />
        </div>
      )}
    </>
  );
}

export function DeviceReadings(): React.JSX.Element {
  const me = usePortalMe();
  const admin = portalIsAdmin(me.data);
  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>Device readings</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        Tank levels your own systems send us: from MQTT gateways and from API keys.
      </p>
      {me.isError ? (
        <p role="alert">Could not load your account.</p>
      ) : me.data === undefined ? (
        <p role="status">Loading…</p>
      ) : admin ? (
        <DeviceReadingsBody />
      ) : (
        <p role="status">{NOT_ADMIN_MESSAGE}</p>
      )}
    </main>
  );
}
